import { useEffect, useState } from 'react';

import type { UseRemoteSpeakingParams } from '../model/types';

/** Чувствительность детектора: выше — строже, ниже — «загорается» чаще. */
export const REMOTE_SPEAKING_THRESHOLD = 0.02;

/**
 * Отслеживает речь удалённого собеседника по аудио-треку video-элемента.
 * Использует Web Audio API (RMS) и пересоздаёт анализатор при замене треков
 * в том же MediaStream.
 */
export const useRemoteSpeaking = ({ remoteVideoRef, isRemoteMute }: UseRemoteSpeakingParams) => {
	const [isSpeaking, setIsSpeaking] = useState(false);

	useEffect(() => {
		const videoEl = remoteVideoRef.current;
		if (!videoEl) return;

		let cleanupAnalyser: (() => void) | null = null;
		let cleanupTrackListeners: (() => void) | null = null;
		let cleanupStreamListeners: (() => void) | null = null;

		const stop = () => {
			cleanupAnalyser?.();
			cleanupAnalyser = null;
			cleanupTrackListeners?.();
			cleanupTrackListeners = null;
			cleanupStreamListeners?.();
			cleanupStreamListeners = null;
		};

		const startAnalyser = (audioTrack: MediaStreamTrack) => {
			const AudioContextCtor =
				window.AudioContext ||
				(window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
			if (!AudioContextCtor) return;

			const audioContext = new AudioContextCtor();
			const analyser = audioContext.createAnalyser();
			analyser.fftSize = FFT_SIZE;

			const source = audioContext.createMediaStreamSource(new MediaStream([audioTrack]));
			source.connect(analyser);

			const dataArray = new Uint8Array(analyser.fftSize);
			let rafId: number | null = null;
			let stopped = false;

			const updateSpeaking = () => {
				if (stopped) return;

				analyser.getByteTimeDomainData(dataArray);

				let sumSquares = 0;
				for (const element of dataArray) {
					const deviation = element - 128;
					sumSquares += deviation * deviation;
				}

				// RMS: больше — громче; сравниваем с настраиваемым порогом
				const rms = Math.sqrt(sumSquares / dataArray.length) / 128;
				const speaking = rms > REMOTE_SPEAKING_THRESHOLD && !audioTrack.muted;

				setIsSpeaking((prev) => (prev === speaking ? prev : speaking));

				rafId = requestAnimationFrame(updateSpeaking);
			};

			audioContext
				.resume()
				.catch(() => null)
				.then(() => {
					if (!stopped) updateSpeaking();
				});

			// Трек может быть заменён внутри того же стрима — пересоздаём анализатор
			const handleTrackUpdate = () => {
				if (!stopped) {
					setIsSpeaking(false);
					handleStreamChange();
				}
			};

			audioTrack.addEventListener('ended', handleTrackUpdate);
			audioTrack.addEventListener('mute', handleTrackUpdate);
			audioTrack.addEventListener('unmute', handleTrackUpdate);

			cleanupTrackListeners = () => {
				audioTrack.removeEventListener('ended', handleTrackUpdate);
				audioTrack.removeEventListener('mute', handleTrackUpdate);
				audioTrack.removeEventListener('unmute', handleTrackUpdate);
			};

			cleanupAnalyser = () => {
				stopped = true;
				if (rafId !== null) {
					cancelAnimationFrame(rafId);
				}
				cleanupTrackListeners?.();
				source.disconnect();
				analyser.disconnect();
				audioContext.close();
			};
		};

		function handleStreamChange() {
			stop();

			if (isRemoteMute) {
				setIsSpeaking(false);
				return;
			}

			const stream = videoEl?.srcObject as MediaStream | null;
			if (!stream) {
				setIsSpeaking(false);
				return;
			}

			// Слушаем add/remove, чтобы подхватить новые дорожки того же MediaStream
			stream.addEventListener('addtrack', handleStreamChange);
			stream.addEventListener('removetrack', handleStreamChange);
			cleanupStreamListeners = () => {
				stream.removeEventListener('addtrack', handleStreamChange);
				stream.removeEventListener('removetrack', handleStreamChange);
			};

			const audioTrack = stream
				.getAudioTracks()
				.find((track) => track.readyState === 'live' && track.enabled);

			if (!audioTrack) {
				setIsSpeaking(false);
				return;
			}

			startAnalyser(audioTrack);
		}

		handleStreamChange();

		videoEl.addEventListener('loadedmetadata', handleStreamChange);
		videoEl.addEventListener('emptied', handleStreamChange);

		return () => {
			videoEl.removeEventListener('loadedmetadata', handleStreamChange);
			videoEl.removeEventListener('emptied', handleStreamChange);
			stop();
		};
	}, [isRemoteMute, remoteVideoRef]);

	return isSpeaking;
};
const FFT_SIZE = 512;
